import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Phone,
  MessageSquare,
  Refrigerator,
  WashingMachine,
  Wind,
  Utensils,
  Flame,
  Microwave,
  ShieldCheck,
  Clock,
  BadgeDollarSign,
  Wrench,
  Sparkles,
  CheckCircle2,
  Star,
  ArrowRight,
  CalendarCheck,
  UserCheck,
  ThumbsUp,
} from "lucide-react";
import { BUSINESS } from "@/lib/contact";
import { BrandWall } from "@/components/BrandWall";
import heroImg from "@/assets/hero-technician.webp";

export const Route = createFileRoute("/")({
  component: Home,
  head: () => ({
    meta: [
      { title: "StillFusion Appliance Repair | Sacramento, CA" },
      {
        name: "description",
        content:
          "Professional appliance repair in Sacramento, CA. Fast service for refrigerators, washers, dryers, dishwashers, ovens and microwaves.",
      },
      { property: "og:title", content: "StillFusion Appliance Repair | Sacramento, CA" },
      { property: "og:description", content: "Professional appliance repair in Sacramento, CA. Fast service for refrigerators, washers, dryers, dishwashers, ovens and microwaves." },
      { property: "og:url", content: "https://stillfusionappliance.com/" },
      { property: "og:image", content: heroImg },
      { name: "twitter:image", content: heroImg },
    ],
    links: [
      { rel: "canonical", href: "https://stillfusionappliance.com/" },
      { rel: "preload", as: "image", href: heroImg, fetchpriority: "high" } as unknown as { rel: string; href: string },
    ],
  }),
});

const services = [
  { icon: Refrigerator, title: "Refrigerator Repair", issues: "Not cooling, leaking, or making noise?" },
  { icon: WashingMachine, title: "Washer Repair", issues: "Won't drain, spin, or turn on?" },
  { icon: Wind, title: "Dryer Repair", issues: "Not heating, tumbling, or taking too long?" },
  { icon: Utensils, title: "Dishwasher Repair", issues: "Not draining, not cleaning, or leaking?" },
  { icon: Flame, title: "Oven / Stove Repair", issues: "Burners won't light or oven won't heat?" },
  { icon: Microwave, title: "Microwave Repair", issues: "Not heating, sparking, or won't turn on?" },
];

const why = [
  { icon: Clock, title: "Fast Service", text: "We arrive fast with fully stocked trucks." },
  { icon: Wrench, title: "Experienced Techs", text: "Factory-trained and background-checked." },
  { icon: BadgeDollarSign, title: "Affordable Pricing", text: "Upfront quotes before we start any work." },
  { icon: ShieldCheck, title: "Warranty Included", text: "Parts and labor backed by our guarantee." },
];

const steps = [
  { icon: CalendarCheck, title: "Book Online", text: "Tell us what's wrong and schedule a convenient time." },
  { icon: UserCheck, title: "Technician Arrives", text: "We diagnose the issue and provide an upfront price." },
  { icon: ThumbsUp, title: "Repair Completed", text: "We fix it on the spot using high-quality parts." },
];

const reviews = [
  {
    text: "My fridge stopped cooling on a Friday night. StillFusion was here Saturday morning and had it fixed in an hour. Lifesavers!",
    name: "Sarah J.",
    location: "Sacramento, CA",
  },
  {
    text: "Honest pricing and very professional. The tech explained exactly what was wrong with my dryer before starting.",
    name: "Mike T.",
    location: "Roseville, CA",
  },
  {
    text: "I've used them twice now (oven and washer). Always on time, clean up after themselves, and the repairs hold up.",
    name: "Elena R.",
    location: "Folsom, CA",
  },
];

const cities = ["Sacramento", "Antelope", "Citrus Heights", "Roseville", "Carmichael", "Folsom", "Elk Grove", "Rocklin"];

function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-hero-gradient text-white">
        <img
          aria-hidden="true"
          alt=""
          src={heroImg}
          fetchPriority="high"
          decoding="async"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, oklch(0.16 0.05 250 / 92%) 0%, oklch(0.16 0.05 250 / 70%) 55%, oklch(0.16 0.05 250 / 35%) 100%)",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-5 py-20 sm:py-28 lg:py-32">
          <div className="max-w-2xl animate-float-in">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white ring-1 ring-white/20 backdrop-blur">
              <Clock className="h-3.5 w-3.5" /> Fast & Reliable Service
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Appliance Repair <br />
              You Can <span className="text-[oklch(0.78_0.18_47)]">Trust</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-white/80">
              {BUSINESS.name} provides professional, reliable, and fast service.
              We specialize in same-day appliance repair for all major household
              brands across the Sacramento area.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
              >
                <CalendarCheck className="h-5 w-5" /> Book Service
              </Link>
              <a
                href={`tel:${BUSINESS.phone}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15"
              >
                <Phone className="h-5 w-5" /> Call Now
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/85">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[oklch(0.78_0.18_47)]" /> Licensed & Insured
              </span>
              <span className="inline-flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[oklch(0.78_0.18_47)]" /> 3+ Years Experience
              </span>
              <span className="inline-flex items-center gap-2">
                <Star className="h-4 w-4 fill-[oklch(0.78_0.18_47)] text-[oklch(0.78_0.18_47)]" /> 5.0 Rating
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <SectionHeader
          eyebrow="Our Services"
          title="What Do You Need Fixed?"
          subtitle="We service all major brands and models. Click a service to learn more."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(({ icon: Icon, title, issues }) => (
            <Link
              key={title}
              to="/services"
              className="surface-card group flex flex-col p-6 hover:-translate-y-1"
            >
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{issues}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                Learn about {title}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
        <p className="mt-10 text-center text-sm text-muted-foreground">
          Don't see your appliance? We fix freezers, ice makers, wine coolers, and more.{" "}
          <Link to="/services" className="font-semibold text-primary hover:underline">
            View all services →
          </Link>
        </p>
      </section>

      {/* WHY US */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                Why choose us
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                Why Choose StillFusion?
              </h2>
              <p className="mt-3 max-w-xl text-muted-foreground">
                When your appliance breaks, you need a reliable team that respects
                your time and your home. We've built our reputation on honest
                pricing and quality workmanship.
              </p>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                {why.map(({ icon: Icon, title, text }) => (
                  <div key={title} className="flex gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-accent/15 text-accent">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-semibold">{title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="btn-primary mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
              >
                Book a Free Estimate <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <div className="surface-elevated overflow-hidden">
                <img
                  src={heroImg}
                  alt="Professional appliance repair technician"
                  className="aspect-[5/4] w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
              <div className="surface-elevated absolute -bottom-6 -left-6 p-5 mx-[8px]">
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent text-accent-foreground">
                    <CheckCircle2 className="h-6 w-6" />
                  </span>
                  <div>
                    <div className="text-2xl font-bold leading-none">
                      <CountUp end={1000} duration={2000} />+
                    </div>
                    <div className="text-xs text-muted-foreground">Repairs Completed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <SectionHeader
          eyebrow="How It Works"
          title="Three simple steps"
          subtitle="Get your appliance running like new — quickly and stress-free."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {steps.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="surface-card relative p-7">
              <span className="absolute -top-4 left-7 grid h-9 w-9 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground shadow-md">
                {i + 1}
              </span>
              <Icon className="h-7 w-7 text-primary" />
              <h3 className="mt-4 text-lg font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BRANDS */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <SectionHeader
            eyebrow="Brands"
            title="We service all major brands"
            subtitle="From everyday workhorses to luxury appliances."
          />
          <div className="mt-10">
            <BrandWall />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <SectionHeader
          eyebrow="Reviews"
          title="Don't just take our word for it"
          subtitle="See what your neighbors are saying about our service."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {reviews.map((r) => (
            <div key={r.name} className="surface-card flex flex-col p-6">
              <div className="flex items-center gap-1 text-accent">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground/85">
                "{r.text}"
              </p>
              <div className="mt-5 border-t border-border pt-4">
                <div className="text-sm font-semibold">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.location}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
          >
            Read more reviews <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-24">
        <div className="surface-elevated relative overflow-hidden bg-hero-gradient p-10 text-white sm:p-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ring-white/20">
                <Sparkles className="h-3.5 w-3.5" /> Ready when you are
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                Ready to get your appliance fixed?
              </h2>
              <p className="mt-3 text-white/80">
                We proudly serve {BUSINESS.city} and surrounding areas. Book online
                now to secure your appointment.
              </p>
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm text-white/70">
                {cities.map((c, i) => (
                  <span key={c} className="inline-flex items-center">
                    {c}
                    {i < cities.length - 1 && <span className="ml-3 text-white/30">•</span>}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to="/contact"
                className="btn-accent inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
              >
                <CalendarCheck className="h-5 w-5" /> Book Service
              </Link>
              <a
                href={`tel:${BUSINESS.phone}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3.5 text-sm font-semibold backdrop-blur transition hover:bg-white/15"
              >
                <Phone className="h-5 w-5" /> Call Now
              </a>
              <a
                href={`sms:${BUSINESS.sms}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3.5 text-sm font-semibold backdrop-blur transition hover:bg-white/15"
              >
                <MessageSquare className="h-5 w-5" /> Text Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="text-center">
      {eyebrow && (
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          {eyebrow}
        </span>
      )}
      <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
      {subtitle && (
        <p className="mt-3 text-base text-muted-foreground sm:text-lg">{subtitle}</p>
      )}
    </div>
  );
}

function CountUp({ end, duration = 2000 }: { end: number; duration?: number }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const startTime = performance.now();
            const tick = (now: number) => {
              const progress = Math.min((now - startTime) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(Math.round(end * eased));
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{value}</span>;
}
